// Island archetypes. Each one is the same core (spawn, arena, main street, saloon-door gate:
// sim/run.ts and the town view depend on those) dropped into a dramatically different piece of
// land, laid out for the jetpack:
//
//   THE FALLS     a meadow island: a spring on the mesa feeds a stream that runs the length of
//                 the north meadow under a stone arch and pours off the edge; buttes to snipe from.
//   THE CANYON    the biggest island: a 13 m deep canyon cuts it end to end with a river on the
//                 floor (the cooling highway: fly the canyon at skim height and you never heat up),
//                 natural bridges to fly under, spires rising out of the river, a hoodoo field.
//   THE SHATTERS  a small core plate and five floating plates at different heights. A lake on the
//                 high plate spills a waterfall down into the gap; floating shelves are stepping
//                 stones between plates. Positioning is three-dimensional here.
//   THE LAGOON    a big still lake (the skim playground) with a rock tower standing in it, shelves
//                 hanging low over the water, and two outlets that fall off the island.
//   THE BASTION   the boss island: a moat curls around the arena (skim it to cool while you fight),
//                 tall spires to break the boss's line of sight, shelves over the arena.
//
// Positions are cm int16 on the wire (±327 m), so everything stays inside ±230 m.

import { Rng, len2 } from './core';
import { Arch, NO_GROUND, World } from './world';

type P = { x: number; z: number };

// ---------------------------------------------------------------- land
export function layoutLand(w: World, rng: Rng) {
  const s = w.seed;
  const isl = (cx: number, cz: number, baseY: number, radius: number, k: number, main = false) =>
    w.islands.push({ cx, cz, baseY, radius, seed: s ^ (0x1111 * k), main });
  const butte = (x: number, z: number, r: number, h: number, soft = 1.2) =>
    w.plateaus.push({ x: x + rng.range(-3, 3), z: z + rng.range(-3, 3), r, h: h * rng.range(0.9, 1.15), soft, seed: rng.int(1, 1 << 30) });
  const m = w.mesa;
  switch (w.arch) {
    case Arch.Falls:
      isl(0, 0, 0, 118, 1, true);
      isl(130, -70, 13, 24, 2); isl(-108, 100, 9, 20, 3); isl(22, 142, 19, 17, 4); isl(-132, -62, 15, 15, 5);
      butte(-84, -8, 8, 15);
      butte(62, -64, 10, 11);
      break;
    case Arch.Canyon:
      isl(0, 0, 0, 146, 1, true);
      isl(178, -64, 14, 20, 2); isl(-152, 132, 10, 22, 3); isl(40, 188, 22, 16, 4); isl(-168, -92, 12, 16, 5);
      m.x = -78; m.z = -84; m.r = 16; m.h = 9;
      w.canyons.push({ pts: CANYON, hw: 13, floor: -13, seed: rng.int(1, 1 << 30) });
      butte(-62, 110, 12, 14); butte(30, 114, 10, 18); butte(64, -70, 11, 12); butte(-98, -24, 9, 16);
      break;
    case Arch.Shattered:
      isl(0, 0, 0, 108, 1, true);
      isl(126, -82, 12, 18, 2);
      // the plates: high cistern (north), west shelf, the tall east plate, the low south-east plate, south-west
      isl(-30, 166, 24, 42, 3);
      isl(-178, 30, 10, 34, 4);
      isl(176, 92, 30, 30, 5);
      isl(160, -152, -6, 26, 6);
      isl(-132, -152, 16, 36, 7);
      butte(70, -52, 9, 12);
      butte(-150, -160, 8, 12);
      break;
    case Arch.Lagoon:
      isl(0, 0, 0, 128, 1, true);
      isl(150, -92, 12, 20, 2); isl(-142, 112, 9, 18, 3); isl(34, 164, 24, 16, 4); isl(-150, -70, 15, 15, 5);
      m.x = -86; m.z = -8; m.r = 15; m.h = 8;
      butte(-104, -58, 10, 13); butte(72, -62, 12, 10);
      // the lagoon sits in a broad bowl
      w.plateaus.push({ x: -16, z: 64, r: 30, h: -4.5, soft: 10, seed: rng.int(1, 1 << 30) });
      break;
    case Arch.Bastion:
    default:
      isl(0, 0, 0, 120, 1, true);
      isl(128, -68, 13, 24, 2); isl(-104, 96, 9, 20, 3); isl(22, 136, 19, 17, 4); isl(-128, -60, 15, 15, 5);
      m.x = -86; m.z = 26; m.r = 14; m.h = 9;
      butte(64, -56, 9, 12);
      break;
  }
}

const CANYON: P[] = [
  { x: -175, z: 60 }, { x: -110, z: 74 }, { x: -52, z: 62 }, { x: -4, z: 78 }, { x: 46, z: 68 }, { x: 98, z: 82 }, { x: 175, z: 70 },
];

// ---------------------------------------------------------------- water
export function layoutWater(w: World, rng: Rng) {
  const j = (p: P[], a = 3) => p.map((q, i) => (i === 0 || i === p.length - 1 ? q : { x: q.x + rng.range(-a, a), z: q.z + rng.range(-a, a) }));
  switch (w.arch) {
    case Arch.Falls: {
      // the mesa spring runs north-east the length of the meadow, through a swimming hole, off the edge
      const spring = w.addPond(-44, 42, 6, 1.4);
      w.addStream(j([{ x: -44, z: 42 }, { x: -30, z: 60 }, { x: -6, z: 72 }, { x: 22, z: 66 }, { x: 54, z: 54 }, { x: 86, z: 58 }, { x: 140, z: 66 }]), 3.4, 1.0,
        spring?.y, [{ x: 22, z: 66, r: 10 }]);
      // the south brook: a pond below spawn spills south-west off the island
      const sp = w.addPond(-14, -76, 7, 1.3);
      w.addStream(j([{ x: -14, z: -76 }, { x: -40, z: -90 }, { x: -72, z: -100 }, { x: -130, z: -120 }]), 2.8, 0.9, sp?.y);
      break;
    }
    case Arch.Canyon: {
      // the canyon river: a spring pool at the west end, flowing east and out of the canyon mouth
      const src = w.addPond(-100, 72, 8, 1.4);
      w.addStream([{ x: -100, z: 72 }, ...CANYON.slice(2)], 4.6, 1.0, src?.y, [{ x: 46, z: 68, r: 9 }]);
      // the south tarn and its outfall
      const t = w.addPond(-22, -96, 13, 1.6);
      w.addStream(j([{ x: -22, z: -96 }, { x: -12, z: -124 }, { x: 0, z: -170 }]), 3.0, 1.0, t?.y);
      break;
    }
    case Arch.Shattered: {
      // the cistern lake on the high plate spills south into the gap and onto the core plate
      const c = w.addPond(-30, 166, 16, 1.6);
      w.addStream([{ x: -30, z: 166 }, { x: -24, z: 142 }, { x: -18, z: 110 }], 3.6, 1.0, c?.y);
      // where it lands, a pool, and a stream west off the core plate
      const pl = w.addPond(-14, 86, 7, 1.4);
      w.addStream(j([{ x: -14, z: 86 }, { x: -46, z: 82 }, { x: -82, z: 70 }, { x: -140, z: 58 }]), 3.0, 1.0, pl?.y);
      // the tall east plate's spring pours off its south-west lip
      const e = w.addPond(176, 92, 8, 1.3);
      w.addStream([{ x: 176, z: 92 }, { x: 160, z: 76 }, { x: 140, z: 56 }], 2.6, 0.9, e?.y);
      // the south-west plate has a tarn
      w.addPond(-126, -142, 9, 1.4);
      break;
    }
    case Arch.Lagoon: {
      const lag = w.addPond(-16, 64, 26, 2.2);
      const ly = lag?.y;
      w.addStream(j([{ x: 8, z: 66 }, { x: 44, z: 60 }, { x: 80, z: 60 }, { x: 140, z: 76 }]), 3.6, 1.0, ly);
      w.addStream(j([{ x: -40, z: 72 }, { x: -76, z: 86 }, { x: -140, z: 100 }]), 3.0, 1.0, ly);
      // the south marsh: three pools threaded on a brook to the edge
      w.addStream(j([{ x: -40, z: -92 }, { x: -18, z: -82 }, { x: 4, z: -92 }, { x: 16, z: -122 }, { x: 24, z: -160 }]), 2.6, 0.9, undefined,
        [{ x: -40, z: -92, r: 6 }, { x: -18, z: -82, r: 7 }, { x: 4, z: -92, r: 6 }]);
      break;
    }
    case Arch.Bastion:
    default: {
      // the moat: a spring north of the arena curls west and south around it, then off the edge
      const sp = w.addPond(-4, 52, 7, 1.4);
      w.addStream([{ x: -4, z: 52 }, { x: -30, z: 44 }, { x: -46, z: 16 }, { x: -44, z: -14 }, { x: -26, z: -40 }, { x: 4, z: -48 }, { x: 32, z: -60 }, { x: 60, z: -88 }, { x: 96, z: -126 }], 3.4, 1.1, sp?.y);
      break;
    }
  }
}

// ---------------------------------------------------------------- landforms
export function layoutForms(w: World, rng: Rng) {
  let v = 0;
  const ground = (x: number, z: number, r: number, hi: boolean) => {
    let g = w.terrain(x, z);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const h = w.terrain(x + Math.cos(a) * r, z + Math.sin(a) * r);
      if (h === NO_GROUND) continue;
      g = g === NO_GROUND ? h : hi ? Math.max(g, h) : Math.min(g, h);
    }
    return g;
  };
  /** a rock stack you can stand on top of: perch, cover, something to orbit */
  const spire = (x: number, z: number, r: number, h: number) => {
    x += rng.range(-2, 2); z += rng.range(-2, 2);
    if (w.terrain(x, z) === NO_GROUND || w.townMask(x, z) > 0.01) return;
    const lo = ground(x, z, r, false), hi = ground(x, z, r, true);
    w.cyls.push({ kind: 'spire', x, z, r, y0: lo - 1.5, y1: hi + h * rng.range(0.9, 1.1), variant: v++ });
  };
  /** a stone arch on two legs; `alongX` = the span runs along x (you fly under it along z) */
  const arch = (x: number, z: number, alongX: boolean, span: number, clear: number, thick = 2.6, width = 5.6) => {
    const ax = alongX ? span / 2 : 0, az = alongX ? 0 : span / 2;
    const legR = 2.2; // narrower than the deck, so it reads as a natural arch, not a stone table
    const ga = ground(x - ax, z - az, legR, true), gb = ground(x + ax, z + az, legR, true);
    if (ga === NO_GROUND || gb === NO_GROUND) return;
    const y0 = Math.max(ga, gb) + clear;
    const id = v++;
    w.boxes.push({ kind: 'arch', x, z, hx: alongX ? span / 2 + legR : width / 2, hz: alongX ? width / 2 : span / 2 + legR, y0, y1: y0 + thick, variant: id });
    for (const s of [-1, 1]) {
      const lx = x + s * ax, lz = z + s * az;
      w.cyls.push({ kind: 'archleg', x: lx, z: lz, r: legR, y0: ground(lx, lz, legR, false) - 1.2, y1: y0 + 0.4, variant: id });
    }
  };
  /** a natural bridge over a canyon: rim to rim, its deck at rim height */
  const bridge = (x: number, z: number, span: number, width = 6, thick = 4) => {
    const ga = w.terrain(x, z - span / 2), gb = w.terrain(x, z + span / 2);
    if (ga === NO_GROUND || gb === NO_GROUND) return;
    const top = Math.min(ga, gb) + 0.1;
    w.boxes.push({ kind: 'arch', x, z, hx: width / 2, hz: span / 2 + 1.5, y0: top - thick, y1: top, variant: v++ });
  };
  /** a floating rock shelf: stand on it, fly under it */
  const shelf = (x: number, z: number, r: number, lift: number, thick = 2.4) => {
    let g = w.terrain(x, z);
    const wy = w.waterAt(x, z);
    if (g === NO_GROUND) g = 0;
    if (wy !== NO_GROUND) g = Math.max(g, wy);
    const top = g + lift;
    const id = v++;
    w.cyls.push({ kind: 'shelf', x, z, r, y0: top - thick, y1: top, variant: id });
    // the keel: the shelf's underside tapers to a hanging point (floating_island.png in miniature);
    // it's solid, so you fly round it, not through it
    const keel = Math.min(lift - thick - 2.6, r * 0.9);
    if (keel > 1) w.cyls.push({ kind: 'keel', x, z, r: r * 0.42, y0: top - thick - keel, y1: top - thick + 0.2, variant: id });
  };
  const hoodoos = (cx: number, cz: number, n: number, spread: number) => {
    for (let i = 0; i < n; i++) {
      const a = rng.range(0, Math.PI * 2), d = rng.range(3, spread);
      const x = cx + Math.cos(a) * d, z = cz + Math.sin(a) * d;
      if (!w.clearOf(x, z, 2.4, 2) || !w.dryAt(x, z)) continue;
      spire(x, z, rng.range(1.5, 2.6), rng.range(5, 11));
    }
  };

  switch (w.arch) {
    case Arch.Falls:
      arch(-6, 72, false, 20, 9);
      spire(-72, 72, 4.6, 26); spire(44, 94, 4, 32); spire(78, -36, 3.4, 20); spire(-102, 40, 3.8, 18);
      shelf(18, -46, 7, 12); shelf(-60, 14, 6, 14); shelf(-12, 44, 8, 10);
      hoodoos(-30, -96, 4, 12);
      break;
    case Arch.Canyon:
      bridge(-52, 62, 30); bridge(46, 68, 32, 7); bridge(118, 79, 30, 5, 6);
      spire(20, 74, 4.6, 30); spire(-80, 66, 3.6, 22);
      shelf(70, 76, 7, 22); shelf(-24, 70, 6, 24); shelf(-104, 104, 6, 12);
      hoodoos(46, -48, 9, 20);
      hoodoos(-40, -130, 4, 10);
      break;
    case Arch.Shattered:
      // stepping stones from the core out to the plates
      shelf(102, 76, 6, 8); shelf(134, 84, 5, 20);
      shelf(-84, -96, 6, 6); shelf(-106, -122, 5, 13);
      shelf(-120, 28, 6, 6); shelf(-60, 120, 6, 12);
      shelf(122, -122, 5, 2);
      spire(42, -60, 4.4, 28); spire(-36, 186, 4, 18); spire(186, 104, 3.6, 16); spire(-186, 40, 4.2, 22);
      arch(-46, 82, false, 18, 8);
      hoodoos(-132, -164, 5, 12);
      hoodoos(160, -152, 4, 10);
      break;
    case Arch.Lagoon: {
      // the tower standing in the lagoon, and shelves hanging low over the water
      spire(-16, 64, 6, 34);
      shelf(-36, 48, 6, 7); shelf(4, 82, 5, 12); shelf(-30, 86, 4.5, 4.5);
      arch(44, 60, false, 18, 8);
      spire(-78, 50, 4, 24); spire(64, 98, 3.8, 20); spire(50, -92, 4, 26);
      hoodoos(-56, -110, 5, 12);
      break;
    }
    case Arch.Bastion:
    default:
      spire(44, 44, 4.4, 24); spire(-54, 56, 4.6, 30); spire(-66, -6, 4, 22); spire(-16, -76, 4.2, 26);
      arch(-46, 4, true, 18, 8);
      shelf(-18, 22, 5, 10); shelf(20, -22, 5, 10);
      break;
  }
  void len2;
}
